import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { copyToClipboard } from '../../utils/clipboard';
import {
  ArtifactApiError,
  ArtifactShare,
  artifactShareUrl,
  revokeArtifactShare,
  shareArtifact,
} from '../../shared/services/artifactsApi';

interface Props {
  open: boolean;
  onClose: () => void;
  artifactId: string;
  title: string;
  share: ArtifactShare | null;
  onShareChange: (share: ArtifactShare | null) => void;
}

type Access = 'public' | 'private';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_EMAILS = 50;

/** Separa o que foi digitado/colado em e-mails válidos e o resto. */
function splitEmails(raw: string): { valid: string[]; invalid: string[] } {
  const valid: string[] = [];
  const invalid: string[] = [];
  raw
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean)
    .forEach((e) => (EMAIL_RE.test(e) ? valid : invalid).push(e));
  return { valid, invalid };
}

const sameList = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join(',') === [...b].sort().join(',');

/** Criar, ajustar o acesso ou desativar o link de um artefato. */
const ArtifactShareDialog: React.FC<Props> = ({ open, onClose, artifactId, title, share, onShareChange }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [access, setAccess] = useState<Access>(share?.access || 'public');
  const [emails, setEmails] = useState<string[]>(share?.allowed_emails || []);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const copiedTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!open) return;
    setAccess(share?.access || 'public');
    setEmails(share?.allowed_emails || []);
    setDraft('');
    setError('');
    setCopied(false);
    setConfirmRevoke(false);
    // `share` muda após salvar: o reset só deve acontecer ao abrir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, busy, onClose]);

  useEffect(() => () => window.clearTimeout(copiedTimer.current), []);

  if (!open) return null;

  const url = share ? artifactShareUrl(share.share_id) : '';
  const pendingEmails = splitEmails(draft);
  const effectiveEmails = Array.from(new Set([...emails, ...pendingEmails.valid]));
  const dirty =
    !share ||
    access !== share.access ||
    (access === 'private' && !sameList(effectiveEmails, share.allowed_emails || []));

  const flashCopied = () => {
    setCopied(true);
    window.clearTimeout(copiedTimer.current);
    copiedTimer.current = window.setTimeout(() => setCopied(false), 2500);
  };

  const commitDraft = (): boolean => {
    if (!draft.trim()) return true;
    const { valid, invalid } = splitEmails(draft);
    const merged = Array.from(new Set([...emails, ...valid]));
    if (merged.length > MAX_EMAILS) {
      setError(`No máximo ${MAX_EMAILS} e-mails.`);
      return false;
    }
    setEmails(merged);
    setDraft(invalid.join(' '));
    if (invalid.length) {
      setError(invalid.length === 1 ? `“${invalid[0]}” não parece um e-mail.` : 'Alguns endereços não parecem e-mails.');
      return false;
    }
    setError('');
    return true;
  };

  const onEmailKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Enter', ',', ';', ' ', 'Tab'].includes(e.key) && draft.trim()) {
      if (e.key !== 'Tab') e.preventDefault();
      commitDraft();
    } else if (e.key === 'Backspace' && !draft && emails.length) {
      setEmails(emails.slice(0, -1));
    }
  };

  const save = async () => {
    if (access === 'private' && !commitDraft()) return;
    const list = access === 'private' ? effectiveEmails : [];
    if (access === 'private' && list.length === 0) {
      setError('Adicione ao menos um e-mail.');
      emailInputRef.current?.focus();
      return;
    }
    setBusy(true);
    setError('');
    try {
      const token = await getAccessTokenSilently();
      const next = await shareArtifact(token, artifactId, access, list);
      onShareChange(next);
      setEmails(next.allowed_emails || []);
      setDraft('');
      if (await copyToClipboard(artifactShareUrl(next.share_id))) flashCopied();
    } catch (e) {
      setError(e instanceof ArtifactApiError && e.message ? e.message : 'Não foi possível salvar o link.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    setError('');
    try {
      const token = await getAccessTokenSilently();
      await revokeArtifactShare(token, artifactId);
      onShareChange(null);
      setConfirmRevoke(false);
      setAccess('public');
      setEmails([]);
    } catch {
      setError('Não foi possível desativar o link.');
    } finally {
      setBusy(false);
    }
  };

  const primaryLabel = busy ? 'Salvando…' : !share ? 'Criar link e copiar' : dirty ? 'Salvar alterações' : 'Concluído';

  const onPrimary = async () => {
    if (dirty) await save();
    else onClose();
  };

  return ReactDOM.createPortal(
    <Overlay onMouseDown={() => !busy && onClose()}>
      <Modal
        role="dialog"
        aria-modal="true"
        aria-labelledby="artifact-share-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Header>
          <HeaderIcon><LinkRoundedIcon /></HeaderIcon>
          <HeaderText>
            <Title id="artifact-share-title">Compartilhar artefato</Title>
            <Subtitle title={title}>{title}</Subtitle>
          </HeaderText>
          <IconBtn onClick={onClose} aria-label="Fechar" disabled={busy}>
            <CloseRoundedIcon fontSize="small" />
          </IconBtn>
        </Header>

        <Body>
          {share && (
            <LinkBox>
              <LinkMeta>
                <Badge $access={share.access}>
                  {share.access === 'private' ? <LockRoundedIcon /> : <PublicRoundedIcon />}
                  {share.access === 'private' ? 'Privado' : 'Público'}
                </Badge>
                <LinkHint>Link ativo</LinkHint>
              </LinkMeta>
              <LinkRow>
                <LinkInput readOnly value={url} onFocus={(e) => e.target.select()} aria-label="Link do artefato" />
                <CopyBtn
                  type="button"
                  $done={copied}
                  onClick={async () => { if (await copyToClipboard(url)) flashCopied(); }}
                >
                  {copied ? <CheckRoundedIcon fontSize="small" /> : <ContentCopyRoundedIcon fontSize="small" />}
                  {copied ? 'Copiado' : 'Copiar'}
                </CopyBtn>
              </LinkRow>
            </LinkBox>
          )}

          <Section>
            <SectionLabel>Quem pode abrir</SectionLabel>
            <Options role="radiogroup" aria-label="Quem pode abrir">
              <Option
                type="button"
                role="radio"
                aria-checked={access === 'public'}
                $active={access === 'public'}
                onClick={() => { setAccess('public'); setError(''); }}
              >
                <OptionIcon $active={access === 'public'}><PublicRoundedIcon fontSize="small" /></OptionIcon>
                <OptionText>
                  <strong>Qualquer pessoa com o link</strong>
                  <span>Não precisa de login.</span>
                </OptionText>
                <RadioDot $active={access === 'public'} />
              </Option>
              <Option
                type="button"
                role="radio"
                aria-checked={access === 'private'}
                $active={access === 'private'}
                onClick={() => { setAccess('private'); setError(''); setTimeout(() => emailInputRef.current?.focus(), 0); }}
              >
                <OptionIcon $active={access === 'private'}><LockRoundedIcon fontSize="small" /></OptionIcon>
                <OptionText>
                  <strong>Só pessoas convidadas</strong>
                  <span>Entram com o e-mail da lista.</span>
                </OptionText>
                <RadioDot $active={access === 'private'} />
              </Option>
            </Options>
          </Section>

          {access === 'private' && (
            <Section>
              <SectionLabel as="label" htmlFor="artifact-share-emails">E-mails</SectionLabel>
              <ChipField $error={Boolean(error)} onClick={() => emailInputRef.current?.focus()}>
                {emails.map((email) => (
                  <Chip key={email}>
                    {email}
                    <ChipRemove
                      type="button"
                      aria-label={`Remover ${email}`}
                      onClick={(e) => { e.stopPropagation(); setEmails(emails.filter((x) => x !== email)); }}
                    >
                      <CloseRoundedIcon />
                    </ChipRemove>
                  </Chip>
                ))}
                <ChipInput
                  id="artifact-share-emails"
                  ref={emailInputRef}
                  type="email"
                  autoComplete="off"
                  placeholder={emails.length ? '' : 'nome@empresa.com — Enter para adicionar'}
                  value={draft}
                  onChange={(e) => { setDraft(e.target.value); if (error) setError(''); }}
                  onKeyDown={onEmailKey}
                  onBlur={() => commitDraft()}
                  onPaste={(e) => {
                    const text = e.clipboardData.getData('text');
                    if (/[\s,;]/.test(text.trim())) {
                      e.preventDefault();
                      setDraft(`${draft} ${text}`);
                      setTimeout(() => commitDraft(), 0);
                    }
                  }}
                />
              </ChipField>
            </Section>
          )}

          {error && <ErrorText role="alert">{error}</ErrorText>}

          <Note>
            <InfoOutlinedIcon />
            <span>O link mostra sempre a versão mais recente. Quem abrir vê só o artefato, não a sua conversa.</span>
          </Note>
        </Body>

        <Footer>
          {share && !confirmRevoke && (
            <GhostDanger type="button" onClick={() => setConfirmRevoke(true)} disabled={busy}>
              Desativar link
            </GhostDanger>
          )}
          {share && confirmRevoke ? (
            <ConfirmRow>
              <span>Quem tiver o link perde o acesso.</span>
              <SecondaryBtn type="button" onClick={() => setConfirmRevoke(false)} disabled={busy}>Cancelar</SecondaryBtn>
              <DangerBtn type="button" onClick={revoke} disabled={busy}>Desativar</DangerBtn>
            </ConfirmRow>
          ) : (
            <PrimaryBtn type="button" onClick={onPrimary} disabled={busy}>
              {primaryLabel}
            </PrimaryBtn>
          )}
        </Footer>
      </Modal>
    </Overlay>,
    document.body,
  );
};

export default ArtifactShareDialog;

// --- estilos (mesmos tokens do ShareModal da conversa) ---

const accent = (theme: any) => theme.blue_color || '#4f46e5';
const border = (theme: any) => theme.user_menu_border || '#e5e7eb';
const muted = (theme: any) => theme.action_icon_color || '#6b7280';
const hover = (theme: any) => theme.hover_overlay_light || '#f3f4f6';
/** Texto legível sobre o acento (no tema escuro o acento é um azul claro). */
const onAccent = (theme: any) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(accent(theme));
  if (!m) return '#fff';
  const n = parseInt(m[1], 16);
  const lum = (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return lum > 0.6 ? '#0b1220' : '#fff';
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.45);
  animation: asdFade 0.15s ease;
  @keyframes asdFade { from { opacity: 0; } to { opacity: 1; } }
`;

const Modal = styled.div`
  width: 480px;
  max-width: 100%;
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 16px;
  background: ${({ theme }) => theme.sidebar_background_solid || '#fff'};
  color: ${({ theme }) => theme.font_color};
  font-family: var(--font-secondary);
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.24);
  animation: asdUp 0.18s ease;
  @keyframes asdUp { from { transform: translateY(8px); opacity: 0; } to { transform: none; opacity: 1; } }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 16px 14px 20px;
`;

const HeaderIcon = styled.div`
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  color: ${({ theme }) => accent(theme)};
  background: ${({ theme }) => `${accent(theme)}1a`};
  svg { font-size: 1.25rem; }
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-family: 'Sora', 'Lexend', sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.3;
`;

const Subtitle = styled.div`
  margin-top: 2px;
  font-size: 0.82rem;
  color: ${({ theme }) => muted(theme)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IconBtn = styled.button`
  flex: none;
  align-self: flex-start;
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => muted(theme)};
  cursor: pointer;
  &:hover:not(:disabled) { background: ${({ theme }) => hover(theme)}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => accent(theme)}; outline-offset: 1px; }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 4px 20px 20px;
  overflow-y: auto;
`;

const LinkBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border-radius: 12px;
  background: ${({ theme }) => hover(theme)};
`;

const LinkMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LinkHint = styled.span`
  font-size: 0.78rem;
  color: ${({ theme }) => muted(theme)};
`;

const Badge = styled.span<{ $access: Access }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 9px 2px 7px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ $access }) => ($access === 'private' ? '#b45309' : '#047857')};
  background: ${({ $access }) => ($access === 'private' ? '#fef3c7' : '#d1fae5')};
  svg { font-size: 0.85rem; }
`;

const LinkRow = styled.div`
  display: flex;
  gap: 8px;
`;

const LinkInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 9px 12px;
  border: 1px solid ${({ theme }) => border(theme)};
  border-radius: 10px;
  background: ${({ theme }) => theme.sidebar_background_solid || '#fff'};
  color: ${({ theme }) => theme.font_color};
  font: 0.82rem ui-monospace, SFMono-Regular, Menlo, monospace;
  outline: none;
  &:focus { border-color: ${({ theme }) => accent(theme)}; }
`;

const CopyBtn = styled.button<{ $done: boolean }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 14px;
  border: none;
  border-radius: 10px;
  font: 600 0.85rem var(--font-secondary);
  color: ${({ $done, theme }) => ($done ? '#fff' : onAccent(theme))};
  background: ${({ $done, theme }) => ($done ? '#16a34a' : accent(theme))};
  cursor: pointer;
  transition: background 0.15s;
  &:focus-visible { outline: 2px solid ${({ theme }) => accent(theme)}; outline-offset: 2px; }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionLabel = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => muted(theme)};
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Option = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  text-align: left;
  border-radius: 12px;
  border: 1.5px solid ${({ $active, theme }) => ($active ? accent(theme) : border(theme))};
  background: ${({ $active, theme }) => ($active ? `${accent(theme)}0d` : 'transparent')};
  color: ${({ theme }) => theme.font_color};
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  &:hover { border-color: ${({ $active, theme }) => ($active ? accent(theme) : muted(theme))}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => accent(theme)}; outline-offset: 2px; }
`;

const OptionIcon = styled.span<{ $active: boolean }>`
  flex: none;
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  color: ${({ $active, theme }) => ($active ? accent(theme) : muted(theme))};
  background: ${({ $active, theme }) => ($active ? `${accent(theme)}1a` : hover(theme))};
`;

const OptionText = styled.span`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  strong { font-size: 0.9rem; font-weight: 600; }
  span { font-size: 0.8rem; color: ${({ theme }) => muted(theme)}; }
`;

const RadioDot = styled.span<{ $active: boolean }>`
  flex: none;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 2px solid ${({ $active, theme }) => ($active ? accent(theme) : border(theme))};
  box-shadow: inset 0 0 0 3px ${({ theme }) => theme.sidebar_background_solid || '#fff'};
  background: ${({ $active, theme }) => ($active ? accent(theme) : 'transparent')};
`;

const ChipField = styled.div<{ $error: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  max-height: 160px;
  overflow-y: auto;
  padding: 6px 8px;
  border-radius: 10px;
  border: 1px solid ${({ $error, theme }) => ($error ? '#dc2626' : border(theme))};
  cursor: text;
  &:focus-within { border-color: ${({ $error, theme }) => ($error ? '#dc2626' : accent(theme))}; }
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  max-width: 100%;
  padding: 3px 4px 3px 10px;
  border-radius: 999px;
  font-size: 0.82rem;
  background: ${({ theme }) => hover(theme)};
  overflow-wrap: anywhere;
`;

const ChipRemove = styled.button`
  display: grid;
  place-items: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: ${({ theme }) => muted(theme)};
  cursor: pointer;
  svg { font-size: 0.85rem; }
  &:hover { color: #dc2626; background: #fee2e2; }
`;

const ChipInput = styled.input`
  flex: 1;
  min-width: 160px;
  padding: 4px;
  border: none;
  outline: none;
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  font: 0.88rem var(--font-secondary);
  &::placeholder { color: ${({ theme }) => muted(theme)}; opacity: 0.8; }
`;

const ErrorText = styled.div`
  margin-top: -8px;
  font-size: 0.82rem;
  color: #dc2626;
`;

const Note = styled.div`
  display: flex;
  gap: 8px;
  font-size: 0.8rem;
  line-height: 1.45;
  color: ${({ theme }) => muted(theme)};
  svg { flex: none; font-size: 1rem; margin-top: 1px; }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 20px;
  border-top: 1px solid ${({ theme }) => border(theme)};
`;

const baseBtn = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 10px;
  font: 600 0.88rem var(--font-secondary);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const PrimaryBtn = styled.button`
  ${baseBtn}
  border: none;
  color: ${({ theme }) => onAccent(theme)};
  background: ${({ theme }) => accent(theme)};
  &:hover:not(:disabled) { filter: brightness(0.94); }
  &:focus-visible { outline: 2px solid ${({ theme }) => accent(theme)}; outline-offset: 2px; }
`;

const SecondaryBtn = styled.button`
  ${baseBtn}
  border: 1px solid ${({ theme }) => border(theme)};
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  &:hover:not(:disabled) { background: ${({ theme }) => hover(theme)}; }
`;

const DangerBtn = styled.button`
  ${baseBtn}
  border: none;
  color: #fff;
  background: #dc2626;
  &:hover:not(:disabled) { background: #b91c1c; }
`;

const GhostDanger = styled.button`
  ${baseBtn}
  margin-right: auto;
  padding-left: 4px;
  padding-right: 4px;
  border: none;
  background: transparent;
  color: #dc2626;
  font-weight: 500;
  &:hover:not(:disabled) { text-decoration: underline; }
`;

const ConfirmRow = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  span {
    margin-right: auto;
    font-size: 0.85rem;
    color: #dc2626;
  }
`;
